/** Settings page metadata and legacy tab mappings (spec §8); no React. */
import type { LucideIcon } from "lucide-react";
import { CONCEPT_ICONS } from "@/components/ui/concept-icons";
import { Icons } from "@/components/ui/icons";
import { translate } from "@/i18n";
import { settingsShellMessages } from "@/i18n/messages/settings-shell";

export type SettingsPageId =
  | "root"
  | "account"
  | "preferences"
  | "plugins"
  | "connections"
  | "projects"
  | "skills"
  | "memory"
  | "help"
  | "account-password"
  | "account-invoices"
  | "account-delete"
  | "preferences-appearance"
  | "preferences-shortcuts"
  | "preferences-shortcut-edit"
  | "preferences-voice"
  | "preferences-voice-prompts"
  | "preferences-voice-prompt-edit"
  | "preferences-voice-vocabulary"
  | "preferences-voice-vocabulary-word"
  | "preferences-sounds"
  | "preferences-screen"
  | "preferences-updates"
  | "preferences-privacy"
  | "preferences-ai-tools"
  | "connections-tool"
  | "connections-app"
  | "connections-browser"
  | "connections-safari"
  | "connections-add-app"
  | "connections-usage"
  | "plugins-browse"
  | "plugins-plugin"
  | "projects-new"
  | "projects-project"
  | "projects-folder"
  | "projects-git"
  | "projects-dev-server"
  | "projects-add-folder"
  | "skills-skill"
  | "skills-customize"
  | "skills-browse"
  | "memory-proposal"
  | "memory-summaries"
  | "help-community";

export type SettingsTarget = {
  page: SettingsPageId;
  args?: Record<string, string>;
};

export type SettingsPlatform = "desktop" | "web";

export type SettingsPageDef = {
  id: SettingsPageId;
  /** In the rendered language, resolved on every read. */
  readonly title: string;
  parent: SettingsPageId | null;
  /** Root pages only: the row icon, also the icon of the page's branch. */
  icon?: LucideIcon;
  platforms?: ReadonlyArray<SettingsPlatform>;
  /** Listed in the search hub's Settings chip. */
  searchable: boolean;
  /** Extra words the search hub matches, beyond the title: the English
   *  title first, so English searches work in every language. */
  keywords?: ReadonlyArray<string>;
};

type SettingsPageSpec = Omit<SettingsPageDef, "title"> & {
  titleKey: Extract<keyof typeof settingsShellMessages.en, `page${string}`>;
};

function pageDef({
  titleKey,
  keywords = [],
  ...spec
}: SettingsPageSpec): SettingsPageDef {
  return {
    ...spec,
    get title() {
      return translate(settingsShellMessages, titleKey);
    },
    keywords: [settingsShellMessages.en[titleKey], ...keywords],
  };
}

const DESKTOP_ONLY: ReadonlyArray<SettingsPlatform> = ["desktop"];

const PAGES: ReadonlyArray<SettingsPageSpec> = [
  { id: "root", titleKey: "pageRoot", parent: null, searchable: false },
  {
    id: "account",
    titleKey: "pageAccount",
    parent: "root",
    icon: Icons.User,
    searchable: true,
    keywords: [
      "profile",
      "billing",
      "plan",
      "subscription",
      "email",
      "name",
      "time zone",
    ],
  },
  {
    id: "account-password",
    titleKey: "pageAccountPassword",
    parent: "account",
    searchable: true,
    keywords: ["security", "sign in"],
  },
  {
    id: "account-invoices",
    titleKey: "pageAccountInvoices",
    parent: "account",
    searchable: true,
    keywords: ["billing", "receipts"],
  },
  {
    id: "account-delete",
    titleKey: "pageAccountDelete",
    parent: "account",
    searchable: true,
    keywords: ["remove", "close account"],
  },
  {
    id: "preferences",
    titleKey: "pagePreferences",
    parent: "root",
    icon: Icons.SlidersHorizontal,
    searchable: true,
  },
  {
    id: "preferences-appearance",
    titleKey: "pagePreferencesAppearance",
    parent: "preferences",
    searchable: true,
    keywords: [
      "theme",
      "dark",
      "light",
      "transparency",
      "opacity",
      "solid",
      "avatars",
    ],
  },
  {
    id: "preferences-voice",
    titleKey: "pagePreferencesVoice",
    parent: "preferences",
    searchable: true,
    keywords: [
      "read aloud",
      "speech",
      "wake word",
      "dictation",
      "microphone",
      "language",
      "dictation anywhere",
      "paste",
    ],
  },
  {
    id: "preferences-voice-prompts",
    titleKey: "pagePreferencesVoicePrompts",
    parent: "preferences-voice",
    platforms: DESKTOP_ONLY,
    searchable: true,
    keywords: [
      "shortcut",
      "prompts",
      "improve with ai",
      "clean up",
      "make concise",
      "translate",
      "bullet points",
      "formal",
    ],
  },
  {
    id: "preferences-voice-prompt-edit",
    titleKey: "pagePreferencesVoicePromptEdit",
    parent: "preferences-voice-prompts",
    platforms: DESKTOP_ONLY,
    searchable: false,
  },
  {
    id: "preferences-voice-vocabulary",
    titleKey: "pagePreferencesVoiceVocabulary",
    parent: "preferences-voice",
    platforms: DESKTOP_ONLY,
    searchable: true,
    keywords: [
      "dictation",
      "words",
      "names",
      "misheard",
      "spelling",
      "custom words",
    ],
  },
  {
    id: "preferences-voice-vocabulary-word",
    titleKey: "pagePreferencesVoiceVocabularyWord",
    parent: "preferences-voice-vocabulary",
    platforms: DESKTOP_ONLY,
    searchable: false,
  },
  {
    id: "preferences-sounds",
    titleKey: "pagePreferencesSounds",
    parent: "preferences",
    searchable: true,
    keywords: ["notifications", "alerts", "volume"],
  },
  {
    id: "preferences-screen",
    titleKey: "pagePreferencesScreen",
    parent: "preferences",
    platforms: DESKTOP_ONLY,
    searchable: true,
    keywords: [
      "pointer",
      "screenshots",
      "permissions",
      "accessibility",
      "control",
    ],
  },
  {
    id: "preferences-shortcuts",
    titleKey: "pagePreferencesShortcuts",
    parent: "preferences",
    searchable: true,
    keywords: ["keyboard", "hotkeys"],
  },
  {
    id: "preferences-shortcut-edit",
    titleKey: "pagePreferencesShortcutEdit",
    parent: "preferences-shortcuts",
    searchable: false,
  },
  {
    id: "preferences-updates",
    titleKey: "pagePreferencesUpdates",
    parent: "preferences",
    platforms: DESKTOP_ONLY,
    searchable: true,
    keywords: ["version", "update", "restart"],
  },
  {
    id: "preferences-privacy",
    titleKey: "pagePreferencesPrivacy",
    parent: "preferences",
    searchable: true,
    keywords: ["analytics", "diagnostics", "recording", "consent"],
  },
  {
    id: "preferences-ai-tools",
    titleKey: "pagePreferencesAiTools",
    parent: "preferences",
    searchable: true,
    keywords: [
      "codex",
      "claude",
      "claude code",
      "opencode",
      "companion",
      "provider",
      "runtime",
      "cli",
      "usage",
    ],
  },
  {
    id: "plugins",
    titleKey: "pagePlugins",
    parent: "root",
    icon: Icons.Package,
    searchable: true,
    keywords: ["packages", "aeo", "seo"],
  },
  {
    id: "plugins-browse",
    titleKey: "pagePluginsBrowse",
    parent: "plugins",
    searchable: true,
    keywords: ["catalog", "marketplace", "install"],
  },
  {
    id: "plugins-plugin",
    titleKey: "pagePluginsPlugin",
    parent: "plugins",
    searchable: false,
  },
  {
    id: "connections",
    titleKey: "pageConnections",
    parent: "plugins",
    searchable: true,
    keywords: [
      "apps",
      "integrations",
      "accounts",
      "browsers",
      "safari",
      "chrome",
      "edge",
      "brave",
      "arc",
      "dia",
    ],
  },
  {
    id: "connections-tool",
    titleKey: "pageConnectionsTool",
    parent: "preferences-ai-tools",
    searchable: false,
  },
  {
    id: "connections-app",
    titleKey: "pageConnectionsApp",
    parent: "connections",
    searchable: false,
  },
  {
    id: "connections-browser",
    titleKey: "pageConnectionsBrowser",
    parent: "connections",
    platforms: DESKTOP_ONLY,
    searchable: false,
  },
  {
    id: "connections-safari",
    titleKey: "pageConnectionsSafari",
    parent: "connections",
    platforms: DESKTOP_ONLY,
    searchable: false,
  },
  {
    id: "connections-add-app",
    titleKey: "pageConnectionsAddApp",
    parent: "connections",
    searchable: true,
    keywords: ["integrations", "connect", "catalog"],
  },
  {
    id: "connections-usage",
    titleKey: "pageConnectionsUsage",
    parent: "preferences-ai-tools",
    searchable: true,
    keywords: ["tokens", "cost", "runs"],
  },
  {
    id: "projects",
    titleKey: "pageProjects",
    parent: "root",
    icon: CONCEPT_ICONS.project,
    searchable: true,
    keywords: ["folders", "git", "dev server"],
  },
  {
    id: "projects-new",
    titleKey: "pageProjectsNew",
    parent: "projects",
    searchable: false,
  },
  {
    id: "projects-project",
    titleKey: "pageProjectsProject",
    parent: "projects",
    searchable: false,
  },
  {
    id: "projects-folder",
    titleKey: "pageProjectsFolder",
    parent: "projects-project",
    searchable: false,
  },
  {
    id: "projects-git",
    titleKey: "pageProjectsGit",
    parent: "projects-folder",
    searchable: false,
  },
  {
    id: "projects-dev-server",
    titleKey: "pageProjectsDevServer",
    parent: "projects-folder",
    searchable: false,
  },
  {
    id: "projects-add-folder",
    titleKey: "pageProjectsAddFolder",
    parent: "projects-project",
    searchable: false,
  },
  {
    id: "skills",
    titleKey: "pageSkills",
    parent: "root",
    icon: CONCEPT_ICONS.skill,
    searchable: true,
    keywords: ["work skills", "agent skills", "library"],
  },
  {
    id: "skills-skill",
    titleKey: "pageSkillsSkill",
    parent: "skills",
    searchable: false,
  },
  {
    id: "skills-customize",
    titleKey: "pageSkillsCustomize",
    parent: "skills-skill",
    searchable: false,
  },
  {
    id: "skills-browse",
    titleKey: "pageSkillsBrowse",
    parent: "skills",
    searchable: true,
    keywords: ["catalog", "add skill"],
  },
  {
    id: "memory",
    titleKey: "pageMemory",
    parent: "root",
    icon: CONCEPT_ICONS.memory,
    searchable: true,
    keywords: ["knowledge", "graph", "remember", "review"],
  },
  {
    id: "memory-proposal",
    titleKey: "pageMemoryProposal",
    parent: "memory",
    searchable: false,
  },
  {
    id: "memory-summaries",
    titleKey: "pageMemorySummaries",
    parent: "memory",
    searchable: true,
    keywords: ["conversations"],
  },
  {
    id: "help",
    titleKey: "pageHelp",
    parent: "root",
    icon: Icons.HelpCircle,
    searchable: true,
    keywords: ["support", "feedback", "privacy", "terms"],
  },
  {
    id: "help-community",
    titleKey: "pageHelpCommunity",
    parent: "help",
    searchable: true,
    keywords: ["linkedin", "youtube", "tiktok", "whatsapp", "contact"],
  },
];

export const SETTINGS_PAGES: Readonly<Record<SettingsPageId, SettingsPageDef>> =
  Object.freeze(
    Object.fromEntries(PAGES.map((page) => [page.id, pageDef(page)])),
  ) as Readonly<Record<SettingsPageId, SettingsPageDef>>;

/** Root rows, in order; each inner list is one section (Help sits after a gap). */
export const SETTINGS_ROOT_GROUPS: ReadonlyArray<
  ReadonlyArray<SettingsPageId>
> = [
  ["account", "preferences", "plugins", "projects", "skills", "memory"],
  ["help"],
];

export {
  REQUIRED_ARGS,
  CARRIED_ARGS,
  TAB_TARGETS,
} from "./settings-page-arguments";

/** The page definition, looked up as an own property only. */
export function settingsPageDef(page: string): SettingsPageDef | undefined {
  return Object.prototype.hasOwnProperty.call(SETTINGS_PAGES, page)
    ? SETTINGS_PAGES[page as SettingsPageId]
    : undefined;
}
